"use client";
import { useThemeStore } from "../../../store";
import { useTheme } from "../../../hooks";
import { themes } from "../../../config/thems";
import {
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  ReferenceLine,
  Brush,
} from "recharts";
import { useMemo } from "react";
import LottieEmptyState from "./no-data-animation/index";

const BookingSummary = ({ bookingSummary = [], height = 200 }) => {
  const { theme: config, setTheme: setConfig } = useThemeStore();
  const { theme: mode } = useTheme();
  const theme = themes.find((theme) => theme.name === config);

  // Parse the JSON string data
  
  const parsedData = useMemo(() => {
    if (!bookingSummary) {
      return [];
    }
    
    try {
      let result;
      // If data is a string, parse it
      if (typeof bookingSummary === 'string') {
        result = JSON.parse(bookingSummary);
      } else {
        result = bookingSummary;
      }
      
      return result;
    } catch (error) {
      console.error('Error parsing bookingSummary data:', error);
      return [];
    }
  }, [bookingSummary]);

  // Process data for chart
  const chartData = useMemo(() => {
    if (!Array.isArray(parsedData) || parsedData.length === 0) {
      return [];
    }

    // Sort data by day order (01, 02, 03, etc.)
    const sortedData = [...parsedData].sort((a, b) => {
      const aDay = parseInt(a.name) || 0;
      const bDay = parseInt(b.name) || 0;
      return aDay - bDay;
    });

    // Transform data for chart
    return sortedData.map(item => ({
      name: item.name,
      Club: Number(item.club) || 0,
      App: Number(item.app) || 0,
      Web: global?.myClub?.isownweb ? (Number(item.web) || 0) : 0,
      MyApp: global?.myClub?.isownapp ? (Number(item.myapp) || 0) : 0,
      WhatsApp: global?.myClub?.isownwhatsapp ? (Number(item.whatsapp) || 0) : 0,
    }));
  }, [parsedData]);

  // Check if there's no data to display
  const hasNoData = !Array.isArray(chartData) || chartData.length === 0;

  if (hasNoData) {
    return (
      <LottieEmptyState
        title="No Booking Data"
        subtitle="Booking summary will appear here when available"
        height={height}
        width={125}
      />
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height} style={{ paddingBottom: "12px" }}>
      <BarChart height={height} data={chartData} margin={{ bottom: 14 }}>
        <CartesianGrid
          stroke={`hsl(${
            theme?.cssVars[mode === "dark" ? "dark" : "light"].chartGird
          })`}
          strokeDasharray="3 3"
          vertical={false}
        />

        <XAxis
          dataKey="name"
          tick={{
            fill: mode === "dark" ? "#cbd5e1" : "#64748b",
            fontSize: "12px",
            textAnchor: "middle",
          }}
          tickLine={false}
          stroke={`hsl(${
            theme?.cssVars[mode === "dark" ? "dark" : "light"].chartGird
          })`}
          axisLine={false}
          label={{
            value: "Days of Month",
            position: "insideBottom",
            offset: -5,
            style: {
              textAnchor: "middle",
              fill: mode === "dark" ? "#cbd5e1" : "#64748b",
              fontSize: "12px",
            },
          }}
        />
        <YAxis
          domain={[0, 'dataMax']}
          tick={{
            fill: mode === "dark" ? "#cbd5e1" : "#64748b",
            fontSize: "12px",
          }}
          tickLine={false}
          stroke={`hsl(${
            theme?.cssVars[mode === "dark" ? "dark" : "light"].chartGird
          })`}
          label={{
            value: "Number of Bookings",
            angle: -90,
            position: "insideLeft",
            style: {
              textAnchor: "middle",
              fill: mode === "dark" ? "#cbd5e1" : "#64748b",
              fontSize: "12px",
            },
          }}
          allowDecimals={false}
          tickCount={6}
        />
        <Legend
          verticalAlign="top"
          wrapperStyle={{ paddingBottom: "10px", paddingLeft: "10px" }}
        />
        <ReferenceLine
          y={0}
          stroke={`hsl(${
            theme?.cssVars[
              mode === "dark" || mode === "system" ? "dark" : "light"
            ].chartGird
          })`}
        />
        <Brush
          dataKey="name"
          height={20}
          y={180}
          stroke={`hsl(${
            theme?.cssVars[mode === "dark" ? "dark" : "light"].info
          })`}
          //style={{ paddingBottom: "10px" }}
        />
        <Bar
          dataKey="App"
          radius={[4, 4, 0, 0]}
          fill={`hsl(${
            theme?.cssVars[mode === "dark" ? "dark" : "light"].primary
          })`}
        />
        <Bar
          dataKey="Club"
          radius={[4, 4, 0, 0]}
          fill={`hsl(${
            theme?.cssVars[mode === "dark" ? "dark" : "light"].info
          })`}
        />
        {global?.myClub?.isownweb && (
          <Bar
            dataKey="Web"
            radius={[4, 4, 0, 0]}
            fill={`hsl(${
              theme?.cssVars[mode === "dark" ? "dark" : "light"].success
            })`}
          />
        )}
        {global?.myClub?.isownapp && (
          <Bar
            dataKey="MyApp"
            radius={[4, 4, 0, 0]}
            fill={`hsl(${
              theme?.cssVars[mode === "dark" ? "dark" : "light"].warning
            })`}
          />
        )}
        {global?.myClub?.isownwhatsapp && (
          <Bar
            dataKey="WhatsApp"
            radius={[4, 4, 0, 0]}
            fill={`hsl(${
              theme?.cssVars[mode === "dark" ? "dark" : "light"].secondary
            })`}
          />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default BookingSummary;
